import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  loadMultiSafepaySdk,
  type MultiSafepayEnv,
  type MultiSafepayInstance,
} from './multisafepayLoader';
import {
  checkoutBrandingCssVars,
  type CheckoutBranding,
} from '../../utils/checkoutBranding';
import { collectBrowserInfo, type CheckoutBrowserInfo } from './browserInfo';
import CheckoutRecap, { type CheckoutRecapRow } from './CheckoutRecap';
import CheckoutStateCard from './CheckoutStateCard';
import useHoldCountdown from './useHoldCountdown';
import './multisafepayCheckout.css';

/** Payment data collected by the component's getPaymentData(). `payload` is opaque — pass untouched. */
export interface ComponentPaymentData {
  gateway: string;
  payload: string;
  tokenize?: boolean;
}

export type CheckoutVariant = 'inline' | 'page';

/**
 * Gateways MultiSafepay paints a QR for. `setQR()` is only meaningful for these: handing it a
 * card order makes the SDK raise "QR could not be loaded", which we then showed the guest as a
 * failed payment — in the same instant `init('redirection')` should have been starting 3-D
 * Secure. Bancontact's id is `MISTERCASH`, not `BANCONTACT`. Mirrors QR_CAPABLE_GATEWAYS in
 * functions/src/utils/multisafepay.ts.
 */
const QR_CAPABLE_GATEWAYS = new Set(['MISTERCASH', 'IDEAL']);

function hasQrCode(gateway?: string | null): boolean {
  return typeof gateway === 'string' && QR_CAPABLE_GATEWAYS.has(gateway.toUpperCase());
}

export interface MultiSafepayCheckoutProps {
  env: MultiSafepayEnv;
  /** Short-lived API token minted server-side (never the raw api_key). */
  apiToken: string;
  amountCents: number;
  currency: string;
  /** ISO locale for the component UI, e.g. `en_US`, `nl_NL`, `fr_FR`. */
  locale?: string;
  /** ISO-3166-1 alpha-2 country, filters available methods, e.g. `NL`. */
  country?: string;
  /** Restrict to a single gateway (e.g. `CREDITCARD`); omit to show all methods (drop-in). */
  gateway?: string;
  branding: CheckoutBranding;
  /**
   * `inline` — last step of the booking widget, single column, collapsible recap.
   * `page` — hosted checkout page, two columns with the full recap beside the form.
   */
  variant?: CheckoutVariant;
  /**
   * Show MultiSafepay's express wallet buttons (Apple Pay / Google Pay) in the drop-in.
   *
   * Off by default: express mode probes the hosting page's origin as an Apple Pay merchant
   * domain, so on an origin MultiSafepay has not registered the Connect methods call answers
   * 401 (error 1032) and the whole drop-in fails to load — not just the wallet row. Turn it on
   * only for an origin registered with MultiSafepay.
   *
   * Apple Pay additionally only renders on Safari/iOS with a card in Wallet; on any other
   * browser the row is absent whatever this is set to.
   */
  wallets?: boolean;
  title?: string;
  payButtonLabel?: string;

  /** Booking recap. Omit to render the form on its own (pre-recap behaviour). */
  recapRows?: CheckoutRecapRow[];
  /** Money breakdown shown under the booking rows. */
  moneyRows?: CheckoutRecapRow[];
  /** Collapsed summary line for the inline recap. */
  recapSummaryTitle?: string;
  recapSummarySubtitle?: string;

  /** When the table hold lapses — drives the live countdown and `onHoldExpired`. */
  holdExpiresAt?: string | number | Date | null;
  onHoldExpired?: () => void;

  /** Page variant only. */
  coverImageUrl?: string | null;
  policy?: React.ReactNode;
  terms?: React.ReactNode;
  /** Block the pay button until the terms box is ticked. */
  requireTerms?: boolean;
  /** Shown in the header chip, e.g. `Nederlands`. */
  localeLabel?: string;
  /**
   * How long to wait for the drop-in to signal readiness before calling it a load failure.
   * A rejected API token leaves the SDK silent — no `onLoad`, no `onError` — so without a
   * deadline the guest watches a skeleton animate indefinitely.
   */
  loadTimeoutMs?: number;

  /**
   * Create the order server-side from the component's payment data. Must return the raw provider
   * order object (`response.data`) so the SDK can drive 3-D Secure / issuer redirection.
   *
   * `browser` is the 3-D Secure device fingerprint and must be forwarded to the API alongside the
   * payment data: MultiSafepay cannot start 3-D Secure without it and declines the order on
   * creation (result code 3100), which is every Bancontact payment.
   */
  createOrder: (
    paymentData: ComponentPaymentData,
    browser?: CheckoutBrowserInfo
  ) => Promise<{
    success: boolean;
    order?: Record<string, unknown>;
    error?: string;
  }>;
  /** Fired after the order is created and redirection is initiated — parent should start polling. */
  onSubmitted?: (order: Record<string, unknown>) => void;
  onError?: (message: string) => void;
  /** Optional breadcrumb for hosts (gift shop) — never includes card payloads. */
  onClientLog?: (event: string, fields?: Record<string, unknown>) => void;
  labels?: Partial<{
    securePayment: string;
    securePaymentPage: string;
    genericError: string;
    processing: string;
    loading: string;
    loadingBody: string;
    choosePayment: string;
    paymentMethod: string;
    holdNotice: string;
    verifying: string;
    verifyingBody: string;
    secureCheckout: string;
    totalLabel: string;
    showDetails: string;
    hideDetails: string;
    reservationDeposit: string;
    loadFailed: string;
    loadFailedBody: string;
    tryAgain: string;
  }>;
}

type Phase = 'loading' | 'ready' | 'submitting' | 'redirecting' | 'error' | 'load_failed';

const CONTAINER_ID_PREFIX = 'MultiSafepayPayment';

function formatAmount(amountCents: number, currency: string, locale?: string): string {
  const value = amountCents / 100;
  try {
    return new Intl.NumberFormat(locale?.replace('_', '-') || 'en', {
      style: 'currency',
      currency,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/**
 * Fully branded, embedded MultiSafepay checkout. Renders the payment component inline (no popup,
 * no hosted redirect for the form itself) and skins it with the location's brand via CSS vars.
 *
 * The method list is whatever the drop-in returns for the location and country — nothing about it
 * is hardcoded here; we own the framing, the recap, the amount and the pay button.
 */
const MultiSafepayCheckout: React.FC<MultiSafepayCheckoutProps> = ({
  env,
  apiToken,
  amountCents,
  currency,
  locale,
  country,
  gateway,
  branding,
  variant = 'inline',
  wallets = false,
  title,
  payButtonLabel,
  recapRows,
  moneyRows,
  recapSummaryTitle,
  recapSummarySubtitle,
  holdExpiresAt,
  onHoldExpired,
  coverImageUrl,
  policy,
  terms,
  requireTerms = false,
  localeLabel,
  loadTimeoutMs = 20_000,
  createOrder,
  onSubmitted,
  onError,
  onClientLog,
  labels,
}) => {
  const [phase, setPhase] = useState<Phase>('loading');
  // Bumped by the retry action to re-run the boot effect from scratch.
  const [bootAttempt, setBootAttempt] = useState(0);
  // The SDK callbacks are captured once at init; this keeps them able to read the live phase.
  const phaseRef = useRef<Phase>('loading');
  phaseRef.current = phase;
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(!requireTerms);
  const instanceRef = useRef<MultiSafepayInstance | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  // Unique per mount so React Strict Mode's destroy() of the first instance cannot wipe the
  // second init that reuses a hardcoded `#MultiSafepayPayment` id.
  const containerId = `${CONTAINER_ID_PREFIX}-${useId().replace(/:/g, '')}`;
  // Always points at the latest submitPayment so the SDK onEvent (wallet auto-submit) closure,
  // captured once when the component inits, never calls a stale createOrder.
  const submitPaymentRef = useRef<(qrPaymentData?: ComponentPaymentData) => void>(() => {});

  const cssVars = useMemo(
    () => checkoutBrandingCssVars(branding) as React.CSSProperties,
    [branding]
  );
  const amountText = useMemo(
    () => formatAmount(amountCents, currency, locale),
    [amountCents, currency, locale]
  );
  const holdClock = useHoldCountdown(holdExpiresAt, onHoldExpired);

  const l = {
    securePayment: labels?.securePayment ?? 'Secured by MultiSafepay · PSD2 / 3-D Secure',
    securePaymentPage:
      labels?.securePaymentPage ??
      'Payments processed by MultiSafepay B.V. — 10Seat never sees your card details.',
    genericError: labels?.genericError ?? 'Something went wrong. Please try again.',
    processing: labels?.processing ?? 'Processing your payment…',
    loading: labels?.loading ?? 'Loading secure checkout…',
    loadingBody: labels?.loadingBody ?? 'Fetching the payment methods available for your booking.',
    choosePayment: labels?.choosePayment ?? 'Choose how to pay',
    paymentMethod: labels?.paymentMethod ?? 'Payment method',
    holdNotice: labels?.holdNotice ?? 'Your table is held for',
    verifying: labels?.verifying ?? 'Payment Processing',
    verifyingBody:
      labels?.verifyingBody ??
      'Your payment is being processed. The reservation will be confirmed automatically once payment is verified.',
    secureCheckout: labels?.secureCheckout ?? 'Secure checkout',
    totalLabel: labels?.totalLabel ?? 'Total due now',
    showDetails: labels?.showDetails ?? 'Show details',
    hideDetails: labels?.hideDetails ?? 'Hide details',
    reservationDeposit: labels?.reservationDeposit ?? 'Reservation deposit',
    loadFailed: labels?.loadFailed ?? 'Could not load the payment methods',
    loadFailedBody:
      labels?.loadFailedBody ??
      'The payment provider did not respond. Your table is still held — try again, or contact the restaurant if this keeps happening.',
    tryAgain: labels?.tryAgain ?? 'Try again',
  };

  /** The drop-in never became usable — a terminal state with a retry, not an inline nudge. */
  const failLoad = useCallback(
    (message?: string) => {
      if (!mountedRef.current) return;
      setErrorMsg(message ?? null);
      setPhase('load_failed');
      // The host renders whatever it is handed, so this must be a sentence, not a status code.
      onError?.(message ?? l.loadFailed);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onError, l.loadFailed]
  );

  const fail = useCallback(
    (message: string) => {
      if (!mountedRef.current) return;
      setErrorMsg(message);
      setPhase('error');
      onError?.(message);
    },
    [onError]
  );

  // Build + init the component once we have a token.
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    setPhase('loading');
    setErrorMsg(null);

    // A rejected api_token leaves the SDK silent — no onLoad, no onError. Without this the guest
    // watches the skeleton forever, which reads as "still working" rather than "broken".
    const deadline = window.setTimeout(() => {
      if (cancelled || !mountedRef.current) return;
      if (phaseRef.current === 'loading') failLoad();
    }, loadTimeoutMs);

    async function boot() {
      try {
        const MultiSafepay = await loadMultiSafepaySdk(env);
        if (cancelled) return;

        const orderData = {
          currency,
          amount: amountCents,
          customer: {
            ...(locale ? { locale } : {}),
            ...(country ? { country } : {}),
          },
          payment_options: {
            settings: {
              // Order-level flag (Bancontact docs). Without it the drop-in stays a card form.
              qr: { enabled: true },
              connect: {
                // Default is false: Visa / Mastercard / Maestro / Amex each get a row,
                // matching the hosted popup. `true` collapses them into one "card" option.
                group_cards: false,
                // Autoload QR before an order exists blanks Bancontact (no QR, no card form).
                // QR is painted after Pay via setQR + init('redirection').
                qr: { enabled: true, autoload: false },
                // `connect.express` is a BOOLEAN in MSP's schema, not an object: we sent
                // `{ enabled: false }` for a year, which is a truthy object and never meant
                // "off". Omitted when we do not want wallets, so MSP applies its own default.
                //
                // Express probes the page origin as an Apple Pay merchant domain; on an origin
                // MultiSafepay has not registered, the Connect methods call 401s with 1032 and
                // the whole drop-in fails to load. Hence opt-in per host.
                ...(wallets ? { express: true } : {}),
                // Keep 3-D Secure / issuer checks in a modal so the widget is not
                // replaced by the standalone `/experience-deposit/return` page.
                ...(variant === 'inline' ? { redirect_mode: 'modal' } : {}),
              },
            },
          },
        };

        onClientLog?.('msp.boot', {
          env,
          amountCents,
          currency,
          locale: locale || null,
          country: country || null,
          variant,
          wallets,
          initType: gateway ? 'payment' : 'dropin',
          gateway: gateway || null,
          containerId,
          order: orderData,
        });

        const instance = new MultiSafepay({ env, order: orderData, apiToken });
        instanceRef.current = instance;

        const initType = gateway ? 'payment' : 'dropin';
        const initOptions: Record<string, unknown> = {
          container: `#${containerId}`,
          ...(gateway ? { gateway } : {}),
          onLoad: (state?: unknown) => {
            if (cancelled || !mountedRef.current) return;
            window.clearTimeout(deadline);
            onClientLog?.('msp.onLoad', { phase: phaseRef.current, state });
            setPhase('ready');
          },
          onError: (state: unknown) => {
            if (cancelled || !mountedRef.current) return;
            // eslint-disable-next-line no-console
            console.error('[MultiSafepay] component error', state);
            onClientLog?.('msp.onError', { phase: phaseRef.current, state });
            // Before the drop-in is usable this is a load failure; after, it is an inline nudge.
            if (phaseRef.current === 'loading') failLoad();
            // Once the order is with the SDK, 3-D Secure is in flight and the purchase status
            // is the authority on the outcome. Tearing the form down here told guests their
            // payment had failed while the issuer was still authenticating it.
            else if (phaseRef.current === 'redirecting') return;
            else fail(l.genericError);
          },
          onSelect: (state?: unknown) => {
            onClientLog?.('msp.onSelect', { phase: phaseRef.current, state });
          },
          onValidation: (state?: unknown) => {
            onClientLog?.('msp.onValidation', { phase: phaseRef.current, state });
          },
          // Wallet buttons (Apple/Google Pay) authorize independently of our pay button.
          onEvent: (state: {
            type?: string;
            success?: boolean;
            data?: { paymentData?: unknown; gateway?: string };
          }) => {
            onClientLog?.('msp.onEvent', {
              phase: phaseRef.current,
              type: state?.type || null,
              success: state?.success ?? null,
              gateway: state?.data?.gateway || null,
              hasPaymentData: Boolean(state?.data?.paymentData),
            });
            if (
              state &&
              state.type === 'payment' &&
              state.success &&
              state.data?.paymentData
            ) {
              submitPaymentRef.current();
            }
          },
          onGetQR: (state?: unknown) => {
            // Do not flip to `redirecting` here — that hid the form when Bancontact
            // fired onGetQR without an order (empty panel, Pay gone).
            onClientLog?.('msp.onGetQR', { phase: phaseRef.current, state });
            // With `autoload: false` the component does not fetch a QR itself: it asks us for
            // the order when the guest clicks "show QR", and paints the code from whatever
            // setQR() is handed. Logging the callback and stopping there left the QR button
            // doing nothing. Only from `ready` — an unprompted onGetQR (Bancontact fires one
            // before the form is usable) must not create an order behind the guest's back.
            // The QR state carries its own payment_data (gateway + a short QR payload) — no card
            // input involved. MSP's own examples post exactly this to their server.
            const qrPaymentData = (
              state as { orderData?: { payment_data?: ComponentPaymentData } } | undefined
            )?.orderData?.payment_data;
            if (phaseRef.current === 'ready' && qrPaymentData?.payload) {
              submitPaymentRef.current(qrPaymentData);
            } else if (phaseRef.current === 'ready') {
              onClientLog?.('msp.onGetQR.no_payment_data', {});
            }
          },
        };

        instance.init(initType, initOptions);
        onClientLog?.('msp.init', { initType, container: `#${containerId}` });
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[MultiSafepay] failed to initialize', err);
          onClientLog?.('msp.boot.exception', {
            error: err instanceof Error ? err.message : String(err),
          });
          window.clearTimeout(deadline);
          failLoad();
        }
      }
    }

    void boot();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      window.clearTimeout(deadline);
      try {
        instanceRef.current?.destroy?.();
      } catch {
        /* noop */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, apiToken, amountCents, currency, locale, country, gateway, variant, wallets, containerId, loadTimeoutMs, bootAttempt]);

  /**
   * `qrPaymentData` marks the drop-in's QR affordance rather than the pay button, and carries the
   * payment data `onGetQR` handed us. MultiSafepay's own examples keep the two apart: onGetQR
   * creates the order from `state.orderData` and hands it to setQR, and stops there. Running
   * init('redirection') as well takes the panel over and the QR just painted is never seen.
   */
  const submitPayment = useCallback(async (qrPaymentData?: ComponentPaymentData) => {
    const instance = instanceRef.current;
    if (!instance) {
      onClientLog?.('msp.submit.no_instance', {});
      return;
    }

    const forQr = Boolean(qrPaymentData);
    // Paying by QR means never typing a card, so the empty card fields the drop-in reports are
    // not the guest's problem. Gating the QR on them killed every Bancontact QR click.
    if (!forQr && instance.hasErrors()) {
      onClientLog?.('msp.submit.has_errors', { errors: instance.getErrors?.() });
      // Component surfaces field-level errors inline; nudge above the button, never replace the form.
      setErrorMsg(l.genericError);
      return;
    }

    setPhase('submitting');
    setErrorMsg(null);

    try {
      const paymentData = qrPaymentData ?? instance.getPaymentData();
      onClientLog?.('msp.submit.start', {
        gateway: paymentData?.gateway || null,
        tokenize: paymentData?.tokenize === true,
        payload_len: paymentData?.payload ? String(paymentData.payload).length : 0,
      });
      const response = await createOrder(paymentData, collectBrowserInfo());

      if (!response || !response.success || !response.order) {
        onClientLog?.('msp.submit.rejected', {
          success: response?.success ?? false,
          error: response?.error || null,
        });
        fail(response?.error || l.genericError);
        return;
      }

      // Drives 3-D Secure / iDEAL issuer / bank-transfer or QR instructions. For frictionless
      // payments this resolves immediately and the parent's status polling takes over.
      // Init first so a parent that starts polling on `onSubmitted` cannot unmount the
      // container before the SDK has a place to paint the challenge.
      if (mountedRef.current) setPhase('redirecting');
      const orderKeys = Object.keys(response.order);
      onClientLog?.('msp.submit.order_ok', {
        order_id: response.order.order_id ?? response.order.orderId ?? null,
        status: response.order.status ?? null,
        has_payment_url: Boolean(response.order.payment_url),
        keys: orderKeys,
      });
      // Only Bancontact and iDEAL have a QR. Calling setQR on a card order made the SDK emit
      // "QR could not be loaded", which our onError turned into a guest-facing failure right as
      // 3-D Secure was starting.
      if (forQr && hasQrCode(paymentData?.gateway)) {
        // MSP's own examples pass a wrapper — `setQR({ order })`, not `setQR(order)`.
        const qrSet = instance.setQR?.({ order: response.order });
        onClientLog?.('msp.setQR', {
          gateway: paymentData.gateway,
          ok: qrSet !== false,
          returned: qrSet ?? null,
        });
        // No init('redirection') here: the guest scans the code we just painted, and the
        // parent's status polling carries them to the success page.
        onSubmitted?.(response.order);
        return;
      }
      if (forQr) onClientLog?.('msp.setQR.skipped', { gateway: paymentData?.gateway || null });
      instance.init('redirection', { order: response.order });
      onClientLog?.('msp.init.redirection', {});
      onSubmitted?.(response.order);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[MultiSafepay] submit failed', err);
      onClientLog?.('msp.submit.exception', {
        error: err instanceof Error ? err.message : String(err),
      });
      fail(l.genericError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, fail, onSubmitted, onClientLog]);

  // Keep the ref used by the SDK onEvent closure pointing at the latest submitPayment.
  submitPaymentRef.current = submitPayment;

  const payLabel =
    phase === 'submitting' ? l.processing : payButtonLabel || `Pay ${amountText}`;
  const payDisabled = phase === 'submitting' || (requireTerms && !termsAccepted);
  const showPayButton = phase === 'ready' || phase === 'submitting' || phase === 'error';
  // The drop-in region is pointless once loading has failed; the card replaces it.
  const showDropin = phase !== 'load_failed';

  const recap =
    recapRows && recapRows.length > 0 ? (
      <CheckoutRecap
        rows={recapRows}
        moneyRows={moneyRows}
        totalLabel={l.totalLabel}
        totalValue={amountText}
        variant={variant}
        summaryTitle={recapSummaryTitle}
        summarySubtitle={recapSummarySubtitle}
        labels={{ show: l.showDetails, hide: l.hideDetails }}
      />
    ) : null;

  const holdLine = holdClock ? (
    <>
      {l.holdNotice} <span className="ck-hold-clock">{holdClock}</span>.
    </>
  ) : null;

  /**
   * The drop-in region. The container is mounted for every phase and never conditionally
   * rendered: `init('redirection')` may paint an issuer challenge, QR code or 3-D Secure iframe
   * straight into it, so unmounting it mid-payment would strand the guest. Only our own framing
   * (the "Payment method" header) is dropped once the challenge takes over.
   *
   * Keep the mount node in layout while loading (`display: none` / `hidden` gives the SDK a
   * 0×0 box and it errors before `onLoad`).
   */
  const dropin = (
    <div
      className={
        phase === 'loading'
          ? 'ck-dropin ck-dropin--booting'
          : phase === 'redirecting'
            ? 'ck-dropin ck-dropin--challenge'
            : 'ck-dropin'
      }
    >
      {phase !== 'redirecting' && (
        <div className="ck-dropin-head">
          <span className="ck-dropin-label">{l.paymentMethod}</span>
        </div>
      )}
      <div id={containerId} ref={containerRef} />
    </div>
  );

  const loadingCard =
    phase === 'loading' ? (
      <CheckoutStateCard figure="skeleton" heading={l.loading} body={l.loadingBody} />
    ) : null;

  const loadFailedCard =
    phase === 'load_failed' ? (
      <CheckoutStateCard
        tone="error"
        figure="glyph"
        glyph="!"
        heading={l.loadFailed}
        body={errorMsg || l.loadFailedBody}
        primary={{ label: l.tryAgain, onClick: () => setBootAttempt((n) => n + 1) }}
      />
    ) : null;


  const errorNudge =
    errorMsg && phase !== 'load_failed' ? <div className="ck-error">{errorMsg}</div> : null;

  const payButton = showPayButton ? (
    <button
      type="button"
      className="ck-pay-btn"
      onClick={() => void submitPayment()}
      disabled={payDisabled}
      aria-busy={phase === 'submitting'}
    >
      {payLabel}
    </button>
  ) : null;

  const termsBox =
    terms && requireTerms ? (
      <label className="ck-terms">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
        />
        <span>{terms}</span>
      </label>
    ) : terms ? (
      <p className="ck-policy">{terms}</p>
    ) : null;

  // ---------------------------------------------------------------- page variant
  if (variant === 'page') {
    return (
      <div className="ck-checkout ck-checkout--page" style={cssVars}>
        <header className="ck-page-header">
          <div className="ck-page-brand">
            {branding.logoUrl ? (
              <img
                className="ck-page-logo"
                src={branding.logoUrl}
                alt={branding.name || 'Logo'}
              />
            ) : null}
            {branding.name ? (
              <span className="ck-page-brand-name">{branding.name}</span>
            ) : null}
          </div>
          <div className="ck-page-header-meta">
            <span className="ck-chip">
              <LockIcon />
              {l.secureCheckout}
            </span>
            {localeLabel ? (
              <span className="ck-chip ck-chip--bordered">{localeLabel}</span>
            ) : null}
          </div>
        </header>

        <div className="ck-page-grid">
          <section className="ck-page-recap">
            <div className="ck-intro">
              <span className="ck-eyebrow">{l.reservationDeposit}</span>
              {title ? <h1 className="ck-page-title">{title}</h1> : null}
              {holdLine ? <p className="ck-page-lede">{holdLine}</p> : null}
            </div>

            {coverImageUrl ? (
              <img className="ck-cover" src={coverImageUrl} alt="" aria-hidden="true" />
            ) : null}

            {recap}

            {policy ? <p className="ck-policy">{policy}</p> : null}
          </section>

          <section className="ck-page-pay">
            <div className="ck-page-pay-head">
              <h2>{l.choosePayment}</h2>
              <span className="ck-amount">{amountText}</span>
            </div>

            {loadingCard}
            {loadFailedCard}
            {showDropin && dropin}
            {phase !== 'redirecting' && termsBox}
            {errorNudge}
            {phase !== 'redirecting' && payButton}

            <div className="ck-secure">
              <LockIcon />
              <span>{l.securePaymentPage}</span>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------- inline variant
  return (
    <div className="ck-checkout ck-checkout--inline" style={cssVars}>
      <div className="ck-intro">
        {branding.logoUrl ? (
          <img className="ck-intro-logo" src={branding.logoUrl} alt={branding.name || 'Logo'} />
        ) : null}
        {title ? <h2 className="ck-heading">{title}</h2> : null}
        {holdLine ? <p className="ck-subheading">{holdLine}</p> : null}
      </div>

      {recap}

      {loadingCard}
      {loadFailedCard}
      {showDropin && dropin}
      {phase !== 'redirecting' && termsBox}
      {errorNudge}
      {phase !== 'redirecting' && payButton}

      <div className="ck-secure">
        <LockIcon />
        <span>{l.securePayment}</span>
      </div>
    </div>
  );
};

const LockIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export default MultiSafepayCheckout;
